"use client";

import { useBuilder } from "@/context/BuilderContext";
import { BUILDER_STEPS } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import { useTranslations } from "next-intl";

export function BuilderStepper() {
  const { currentStep, selections, goToStep } = useBuilder();
  const t = useTranslations("builder");

  const stepLabels: Record<string, string> = {
    base_type: t("baseType"),
    texture: t("texture"),
    length: t("length"),
    color: t("color"),
    cap_size: t("capSize"),
    extras: t("extras"),
  };

  const isStepCompleted = (index: number) => {
    const key = BUILDER_STEPS[index].key;
    // Extras step is considered completed if we've gone past it
    if (key === "extras") return currentStep > index;
    return selections[key as keyof typeof selections] !== null;
  };

  const canNavigateTo = (index: number) => {
    // Can always go back to completed steps or current step
    if (index <= currentStep) return true;
    // Can go forward only if all previous steps are completed
    for (let i = 0; i < index; i++) {
      if (!isStepCompleted(i)) return false;
    }
    return true;
  };

  return (
    <div className="max-w-3xl mx-auto overflow-visible">
      <div className="flex items-center justify-between overflow-visible" role="tablist">
        {BUILDER_STEPS.map((step, index) => {
          const completed = isStepCompleted(index);
          const active = index === currentStep;
          const navigable = canNavigateTo(index);

          return (
            <div key={step.key} className="flex items-center flex-1 last:flex-none">
              {/* Step circle + label */}
              <button
                onClick={() => navigable && goToStep(index)}
                disabled={!navigable}
                role="tab"
                aria-label={`Step ${index + 1} of ${BUILDER_STEPS.length}: ${stepLabels[step.key] || step.label}`}
                aria-current={index === currentStep ? "step" : undefined}
                className={cn(
                  "flex flex-col items-center gap-2 group transition-all duration-500",
                  navigable ? "cursor-pointer" : "cursor-default"
                )}
              >
                <div className="relative">
                  {/* Outer glow ring for active state */}
                  <div
                    className={cn(
                      "absolute -inset-1 rounded-full transition-all duration-700 ease-out",
                      active
                        ? "bg-brand-gold/15 scale-100 opacity-100"
                        : "bg-transparent scale-75 opacity-0"
                    )}
                  />
                  <div
                    className={cn(
                      "relative w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs font-medium transition-all duration-700 ease-out border-2",
                      active &&
                        "bg-brand-gold border-brand-gold text-white scale-110 shadow-lg shadow-brand-gold/30",
                      completed &&
                        !active &&
                        "bg-brand-gold/10 border-brand-gold text-brand-gold group-hover:bg-brand-gold/20 group-hover:shadow-sm group-hover:shadow-brand-gold/15",
                      !active &&
                        !completed &&
                        "bg-surface border-theme-border text-foreground-muted"
                    )}
                  >
                    {completed && !active ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>
                </div>
                <span
                  className={cn(
                    "text-[11px] sm:text-xs tracking-wider uppercase font-medium transition-all duration-500 truncate max-w-[60px] sm:max-w-none",
                    active && "text-brand-gold",
                    completed && !active && "text-brand-gold-dark group-hover:text-brand-gold",
                    !active && !completed && "text-foreground-muted"
                  )}
                >
                  {stepLabels[step.key] || step.label}
                </span>
              </button>

              {/* Connecting line */}
              {index < BUILDER_STEPS.length - 1 && (
                <div className="flex-1 px-2 sm:px-4 mb-5 sm:mb-6">
                  <div className="h-[1.5px] bg-theme-border/60 rounded-full relative overflow-hidden">
                    <div
                      className={cn(
                        "absolute inset-y-0 left-0 rounded-full transition-all duration-700 ease-out",
                        completed
                          ? "w-full bg-gradient-to-r from-brand-gold to-brand-gold-light"
                          : "w-0 bg-brand-gold"
                      )}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
